import { CircleNotch } from "@phosphor-icons/react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type AdminHouseholdSummary, getApiErrorMessage, useArchiveHousehold } from "@/lib/api";

interface Props {
  household: AdminHouseholdSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Confirmation before archiving a household — reversible, but a deliberate step. */
export function ArchiveHouseholdDialog({ household, open, onOpenChange }: Props) {
  const archiveMutation = useArchiveHousehold();
  const [error, setError] = useState("");

  const handleArchive = async () => {
    if (!household) {
      return;
    }
    setError("");
    try {
      await archiveMutation.mutateAsync({ id: household.id });
      onOpenChange(false);
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to archive household."));
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setError("");
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Archive household</DialogTitle>
          <DialogDescription>
            Archive <strong>{household?.name}</strong>? It will be hidden from the list. You can
            restore it anytime from the <strong>Archived</strong> filter.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleArchive} disabled={archiveMutation.isPending}>
            {archiveMutation.isPending ? (
              <CircleNotch className="h-4 w-4 animate-spin" weight="light" />
            ) : (
              "Archive"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
